import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bookmark, Sparkles, Trash2, Loader2, ArrowLeft, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSeo } from '../hooks/useSeo'
import { useAuthStore } from '../context/store'
import { customerApi, ApiError, type SavedDesignRecord } from '../utils/customer'

export default function SavedDesignsPage() {
  useSeo({ title: 'Saved Designs', path: '/account/saved-designs', noindex: true })
  const { isAuthenticated, initialized, login } = useAuthStore()
  const navigate = useNavigate()
  const [designs, setDesigns] = useState<SavedDesignRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    if (!initialized) return
    if (!isAuthenticated) {
      login('/account/saved-designs')
      return
    }
    customerApi.designs()
      .then(r => setDesigns(r.designs))
      .catch(err => setError(err instanceof ApiError ? err.message : 'Could not load your designs'))
  }, [initialized, isAuthenticated, login])

  const remove = async (id: string) => {
    if (!confirm('Delete this saved design?')) return
    setDeleting(id)
    try {
      const r = await customerApi.deleteDesign(id)
      setDesigns(r.designs)
      toast.success('Design deleted')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete design')
    } finally {
      setDeleting(null)
    }
  }

  const open = (d: SavedDesignRecord) => navigate('/customize', { state: { savedDesign: d } })

  return (
    <div className="min-h-screen py-8">
      <div className="container-main">
        <Link to="/account" className="inline-flex items-center gap-2 text-brand-gray hover:text-white mb-6 text-sm">
          <ArrowLeft size={16} /> Back to account
        </Link>
        <div className="flex items-center justify-between gap-4 mb-8">
          <h1 className="font-serif text-3xl text-white">Saved Designs</h1>
          {designs && <span className="text-brand-gray text-sm">{designs.length} / 20</span>}
        </div>

        {error && <p className="text-red-400 text-sm mb-6">{error}</p>}

        {!designs && !error && (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand-gold" /></div>
        )}

        {designs && designs.length === 0 && (
          <div className="card p-12 text-center">
            <Bookmark size={40} className="text-brand-gray mx-auto mb-4" />
            <h3 className="font-semibold text-white mb-2">No saved designs yet</h3>
            <p className="text-brand-gray text-sm mb-6">
              Create a custom mat and save your design to come back to it later.
            </p>
            <Link to="/customize" className="btn-primary inline-flex items-center gap-2">
              <Sparkles size={16} /> Create a Design
            </Link>
          </div>
        )}

        {designs && designs.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {designs.map(d => (
              <div key={d.id} className="card overflow-hidden flex flex-col">
                <button onClick={() => open(d)} className="block aspect-[3/2] bg-brand-navy overflow-hidden">
                  <img src={d.preview} alt={d.name} className="w-full h-full object-cover" />
                </button>
                <div className="p-4 flex-1 flex flex-col gap-3">
                  <div>
                    <p className="font-semibold text-white truncate">{d.name}</p>
                    <p className="text-brand-gray text-xs">
                      {[d.variant.size, d.variant.thickness, d.variant.material, d.variant.stitching].filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-brand-gray text-xs mt-1">Saved {new Date(d.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2 mt-auto">
                    <button onClick={() => open(d)} className="btn-primary flex-1 min-h-[44px] text-xs px-3">
                      <Pencil size={14} /> Open
                    </button>
                    <button
                      onClick={() => remove(d.id)}
                      disabled={deleting === d.id}
                      className="btn-ghost min-h-[44px] px-3 text-red-400 hover:text-red-300"
                      title="Delete"
                    >
                      {deleting === d.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
